'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  buildFindingsGroupedRows,
  resolveGroupedRowsLoadMoreIndex,
  toggleIdsInSet,
  type FindingsGridRow,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';
import type { FindingsBulkFilter } from '../../../clients/testerClient';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { useFindingsGroupCounts } from '../../../hooks/api/useFindingsGroupCounts';

export function useFindingsTableModel({
  targetId,
  filter,
  findings,
  groupByField,
  hasNextPage,
}: {
  targetId: string | null | undefined;
  filter: FindingsBulkFilter;
  findings: InternalEnrichedFinding[];
  groupByField: FindingsGroupByField | null;
  hasNextPage: boolean;
}) {
  const tGroupBy = useTranslations('findingsTab.groupBy');
  const dateFormatter = useDateFormatter();
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const { groups, countByKey } = useFindingsGroupCounts({
    targetId,
    filter,
    groupBy: groupByField,
  });
  const labels = useMemo(
    () => ({
      emptyValue: tGroupBy('emptyValue'),
      published: tGroupBy('published'),
      unpublished: tGroupBy('unpublished'),
      formatDateDay: (date: Date) => dateFormatter.formatDateOnly(date),
    }),
    [dateFormatter, tGroupBy],
  );

  useEffect(() => {
    setCollapsedGroupKeys(new Set());
  }, [groupByField]);

  const groupKeys = useMemo(
    () => (groups ?? []).map((group) => group.key),
    [groups],
  );
  const rows = useMemo<FindingsGridRow[]>(
    () =>
      groupByField
        ? buildFindingsGroupedRows({
            findings,
            groupByField,
            collapsedGroupKeys,
            labels,
            serverCountByKey: countByKey,
            knownGroupKeys: groupKeys,
          })
        : findings,
    [collapsedGroupKeys, countByKey, findings, groupByField, groupKeys, labels],
  );
  const loadMoreRowIndex = useMemo(() => {
    if (!groupByField) {
      return rows.length > 0 ? rows.length - 1 : null;
    }

    return resolveGroupedRowsLoadMoreIndex({
      rows,
      collapsedGroupKeys,
      hasUnseenGroups: hasNextPage,
    });
  }, [collapsedGroupKeys, groupByField, hasNextPage, rows]);
  const toggleGroupCollapsed = useCallback(
    (groupKey: string) =>
      setCollapsedGroupKeys((previous) =>
        toggleIdsInSet({
          ids: previous,
          itemIds: [groupKey],
          shouldInclude: !previous.has(groupKey),
        }),
      ),
    [],
  );
  const expandAllGroups = useCallback(
    () => setCollapsedGroupKeys(new Set()),
    [],
  );
  const collapseAllGroups = useCallback(
    () => setCollapsedGroupKeys(new Set(groupKeys)),
    [groupKeys],
  );

  return {
    rows,
    loadMoreRowIndex,
    collapsedGroupKeys,
    toggleGroupCollapsed,
    areAllGroupsCollapsed:
      groupKeys.length > 0 &&
      groupKeys.every((groupKey) => collapsedGroupKeys.has(groupKey)),
    expandAllGroups,
    collapseAllGroups,
  };
}
